<template>
  <p ref="htmlRef" v-html="DOMPurify.sanitize(resolvedHtml)"></p>
  <modal
    v-if="error"
    :closeBtn="true"
    ref="modalRef"
    :styleOptions="styleOptions"
    @close="error = false"
    ><Error></Error
  ></modal>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, computed, onMounted, ref, onUnmounted } from "vue";
import DOMPurify from "dompurify";
import { resolveMergeFields } from "@/use/formatter";
import { ManualIncomeRequest } from "@/models/api";
import { Stipulation } from "@/models/opal";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import Error from "@/components/shared/Error.vue";
import Modal from "@/components/shared/Modal.vue";
import { ModalStyleOptions } from "@/models/modal";

const store = useStore();

interface Props {
  stip: Stipulation;
}

const props = withDefaults(defineProps<Props>(), {});

const htmlRef = ref<HTMLElement | HTMLAnchorElement | HTMLDivElement | null>();
const modalRef = ref<InstanceType<typeof Modal> | null>();

const error = ref<boolean>(false);

const styleOptions = ref<ModalStyleOptions>({
  modalBody: {
    minHeight: "0px",
  },
});
const resolvedHtml = computed<string>(() => {
  // Resolve anchor merge field in the description
  return resolveMergeFields(props.stip.stipulationText, {
    ClickHere: `<a id="click-here"> click here</a>.`,
  });
});

function openErrorModal() {
  error.value = true;
  if (modalRef.value && modalRef.value.modalBody) {
    modalRef.value.modalBody.style.minHeight = "0px !important";
  }
}

const request = computed<ManualIncomeRequest>(() => {
  return {
    applicationId: store.state?.appData?.application?.id,
    lenderId: store.state?.appData?.application?.lenderId,
    programTypeId: store.state?.appData?.application?.programTypeId,
    productTypeId: store.state?.appData?.application?.productTypeId,
    stipulationId: props.stip.id,
    applicantId: props.stip.applicantId,
  };
});

function disableLink(flag: boolean) {
  const clickHere = htmlRef.value?.querySelector("#click-here") as HTMLAnchorElement;
  if (clickHere) {
    if (flag === true) {
      clickHere.style.pointerEvents = "none";
      clickHere.style.cursor = "default"; // Optional: change cursor for visual indication
    } else {
      clickHere.style.pointerEvents = "auto";
      clickHere.style.cursor = "pointer"; // Optional: restore cursor
    }
  }
}

async function handleClickHere(event: Event) {
  event.preventDefault(); // This stops the link from navigating

  disableLink(true);

  const manualIncomeResponse = await $api.income.createManualIncome(request.value);

  if (!manualIncomeResponse.errors || !manualIncomeResponse.error) {
    // Get the latest stips
    await store.dispatch("checklistModule/getSortedChecklistItems");
  } else {
    openErrorModal();
  }

  disableLink(false);
}

onMounted(() => {
  if (htmlRef.value) {
    const clickHere = htmlRef.value.querySelector("a");
    if (clickHere) {
      clickHere.classList.add("sc-link", "sc-link-underline");
      clickHere.addEventListener("click", handleClickHere);
      disableLink(false);
    }
  }
});

onUnmounted(() => {
  if (htmlRef.value) {
    const clickHere = htmlRef.value.querySelector("a");
    if (clickHere) {
      clickHere.removeEventListener("click", handleClickHere);
    }
  }
});
</script>
